import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Search, AlertCircle, ChevronDown, Loader2} from 'lucide-react';
import debounce from 'lodash.debounce';
import dayjs from 'dayjs';
import type {AllClientsQuery} from '../net/types/graphql';

import {useIsAdmin, useAppSelector} from '../state/hooks';
import {useSafeNavigate as useNavigate} from '../util/safeNavigate';
import {useAllClients} from '../net/query/AllClients.gql';
import {useFsmStates} from '../net/query/FsmStates.gql';
import {Input} from '../components/ui/input';
import {Button} from '../components/ui/button';
import {Checkbox} from '../components/ui/checkbox';
import {Label} from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {Skeleton} from '../components/ui/skeleton';

/**
 * How many chat threads to load in a single query.
 *
 * New batches will be loaded as user scrolls.
 */
const CHATS_QUERY_BATCH_SIZE = 20;

/**
 * How often to update UI.
 *
 * NOTE: does not refetch data.
 */
const RERENDER_INTERVAL = 1000 * 60; // 1 minute

/**
 * Abstraction over clients queries that calls the correct query regardless
 * of whether this is an admin or a regular user, and returns results in a
 * consistent format.
 */
const useClients = (search: string, interacted: boolean) => {
  const id = useAppSelector(({auth}) => auth.userId || '');
  const isAdmin = useIsAdmin();

  return useAllClients({
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: CHATS_QUERY_BATCH_SIZE,
      offset: 0,
      search: search || undefined,
      mine: !isAdmin,
      interacted,
      staffId: id,
    },
  });
};

/**
 * List of chat threads ordered by most recent with infinite scrolling.
 */
export const ChatList = () => {
  const isAdmin = useIsAdmin();
  const {accountId} = useParams();
  const rerender = useState({})[1];
  const fsmData = useFsmStates();
  const [search, setSearch] = useState('');
  const [interactedFilter, setInteractedFilter] = useState(false);
  const navigate = useNavigate();
  const {data, refetch, fetchMore, loading} = useClients(
    search,
    interactedFilter,
  );
  const fsmStates = (fsmData.data?.fsm.meta.states || [])
    .slice()
    .sort((a, b) => a.localeCompare(b));

  // Delayed execution of search query
  const refetchForSearch = debounce(
    () =>
      refetch({
        limit: CHATS_QUERY_BATCH_SIZE,
        offset: 0,
        search: search || undefined,
        mine: !isAdmin,
        interacted: interactedFilter,
      }),
    150,
  );

  // Schedule search query when search field changes
  useEffect(() => {
    // Don't run expensive queries for short searches
    if (search && search.length < 3) {
      return;
    }
    refetchForSearch();
  }, [search]);

  // Schedule refetch when interacted filter changes
  useEffect(() => {
    refetchForSearch();
  }, [interactedFilter]);

  // Re-render the chat list periodically to update the timestamps
  useEffect(() => {
    const iv = setInterval(() => rerender({}), RERENDER_INTERVAL);
    return () => {
      clearInterval(iv);
    };
  }, []);

  // Compute offset for fetching next batch of clients.
  const nextBatchOffset = (data?.clients || []).length;
  // Some clients might be null if an error occurred on the backend when trying
  // to load them. Filter them out so that rendering doesn't break.
  const clients = (data?.clients || [])
    .filter((c) => !!c)
    .sort((a, b) => {
      // Sort by most recent messages
      const aSent = a.lastMessage?.sent ? +new Date(a.lastMessage.sent) : null;
      const bSent = b.lastMessage?.sent ? +new Date(b.lastMessage.sent) : null;
      if (!aSent && !bSent) {
        return -1;
      } else if (!aSent) {
        return 1;
      } else if (!bSent) {
        return -1;
      }

      return aSent > bSent ? -1 : 1;
    });

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleFsmStateSelect = (state: string) => {
    setSearch(`state:${state}`);
  };

  const handleLoadMore = () => {
    fetchMore({
      variables: {
        offset: nextBatchOffset,
      },
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search and Filter Section */}
      <div className="p-4 border-b space-y-4">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={
                isAdmin
                  ? 'Search clients by name or ID'
                  : 'Search clients by name'
              }
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-6 w-6">
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleFsmStateSelect('')}>
                  &lt;none&gt;
                </DropdownMenuItem>
                {fsmStates.map((state) => (
                  <DropdownMenuItem
                    key={state}
                    onClick={() => handleFsmStateSelect(state)}
                  >
                    {state}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {isAdmin && (
          <div className="flex items-center space-x-2 px-2">
            <Checkbox
              id="interacted-filter"
              className="h-4 w-4 bg-(--sidebar-primary-foreground) border-(--sidebar-ring) cursor-pointer"
              checked={interactedFilter}
              onCheckedChange={(checked) =>
                setInteractedFilter(checked as boolean)
              }
            />
            <Label
              htmlFor="interacted-filter"
              className="text-xs text-(--sidebar-foreground)/70"
            >
              Hide clients I haven't messaged
            </Label>
          </div>
        )}
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-auto">
        <div className="space-y-1 p-2">
          {clients.map((item) => (
            <ChatListItem
              key={item.id}
              item={item}
              isActive={accountId === item.id}
              onSelect={() => navigate(`/chat/${item.id}`)}
            />
          ))}

          {/* Load More */}
          {data?.hasMoreClients && (
            <div className="p-4 text-center">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading ...
                  </>
                ) : (
                  'Load More'
                )}
              </Button>
            </div>
          )}

          {/* Loading Skeleton */}
          {loading && (
            <div className="space-y-2 p-2">
              {Array.from({length: 3}).map((_, i) => (
                <div key={i} className="flex items-center space-x-3 p-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Individual chat list item component
 */
const ChatListItem = ({
  item,
  isActive,
  onSelect,
}: {
  item: AllClientsQuery['clients'][number];
  isActive: boolean;
  onSelect: () => void;
}) => {
  const isDisabled = !item.isActive;

  return (
    <div
      className={`
        flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors h-16
        hover:bg-(--sidebar-accent) hover:text-color-(--sidebar-accent-foreground)
        ${isActive ? 'bg-(--sidebar-accent) text-color-(--sidebar-accent-foreground)' : ''}
        ${isDisabled ? 'opacity-50' : ''}
      `}
      onClick={onSelect}
    >
      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium truncate">
            {item.fullName || '<Unknown>'}
          </h4>
          <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
            {item.lastMessage?.sent
              ? dayjs(item.lastMessage.sent).fromNow()
              : null}
          </span>
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          {item.lastMessage?.content || ''}
        </p>
      </div>

      {/* Status Icon */}
      {isDisabled && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertCircle className="h-4 w-4 text-destructive" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Failed to fetch information about this person.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
